import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
    addShortInventoryToEquipmentCache,
    equipmentApi,
    removeShortInventoryFromCache,
    updateShortInventoryInCache,
} from '@entities/equipment';
import { equipmentInventoryApi } from '@entities/equipmentInventory';
import { useMoveEquipmentInventoryQuantityContext } from '@features/moveEquipmentInventoryQuantity';
import { showToast } from '@features/ToastManager';

import {
    addMovedInventoryToEquipmentCache,
    partiallyMoveInventoryInEquipmentCache,
} from '../lib/equipmentCache';

import type { EquipmentDataQueryCache, Warehouse } from '@entities/equipment';
import type { EquipmentInventoryStatus } from '@entities/equipmentInventory';
import type { UiDropdownSearchListItem } from '@shared/ui';

type InventoryModalMode = 'create' | 'edit' | 'copy';

const PREVIEW_URL_FIELD = 'preview_url';
const SORT_INDEX_FIELD = 'sort_index';
const FREE_DATE_FIELD = 'free_date';

interface UseWarehouseInventoryActionsParams {
    warehouse: Warehouse;
    warehouses: Warehouse[];
}

export const useWarehouseInventoryActions = ({
    warehouse,
    warehouses,
}: UseWarehouseInventoryActionsParams) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { requestMove } = useMoveEquipmentInventoryQuantityContext();
    const [openedInventoryGuid, setOpenedInventoryGuid] = useState<string | null>(null);
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [inventoryGuidForDelete, setInventoryGuidForDelete] = useState<string | null>(null);
    const [inventoryModalMode, setInventoryModalMode] = useState<InventoryModalMode>('edit');
    const equipmentDataQueryKey = equipmentApi.getEquipmentDataQueryOptions().queryKey;

    const invalidateWarehouseTotals = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
            }),
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                    warehouseId: warehouse.id,
                }).queryKey,
            }),
        ]);
    };

    const invalidateInventoryActionTotals = async (warehouseIds: number[]) => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: equipmentApi.getTotalInfoQueryOptions().queryKey,
            }),
            ...warehouseIds.map((warehouseId) =>
                queryClient.invalidateQueries({
                    queryKey: equipmentApi.getWarehouseTotalInfoQueryOptions({
                        warehouseId,
                    }).queryKey,
                })
            ),
        ]);
    };

    const createInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.createInventory,
        onSuccess: async (data) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                addShortInventoryToEquipmentCache({
                    previous,
                    warehouseId: warehouse.id,
                    inventory: data.data.response,
                })
            );
            setInventoryModalMode('create');
            setOpenedInventoryGuid(data.data.response.guid);
            setIsInventoryModalOpen(true);
            await invalidateWarehouseTotals();
        },
    });

    const copyInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.copyInventory,
        onSuccess: async (data) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                addShortInventoryToEquipmentCache({
                    previous,
                    warehouseId: warehouse.id,
                    inventory: data.data.response,
                })
            );
            setInventoryModalMode('copy');
            setOpenedInventoryGuid(data.data.response.guid);
            setIsInventoryModalOpen(true);
            showToast(t('Board.render.Toast.message'));
            await invalidateWarehouseTotals();
        },
    });

    const moveInventoryMutation = useMutation({
        mutationFn: equipmentApi.moveInventory,
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });
            const previousEquipmentData =
                queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey);
            const movedInventory = warehouse.items.find(
                (item) => item.guid === variables.inventoryGuid
            );

            if (movedInventory) {
                queryClient.setQueryData<EquipmentDataQueryCache>(
                    equipmentDataQueryKey,
                    (previous) =>
                        addMovedInventoryToEquipmentCache({
                            previous,
                            fromWarehouseId: warehouse.id,
                            warehouseId: variables.toWarehouseId,
                            inventoryGuid: variables.inventoryGuid,
                            inventory: {
                                guid: movedInventory.guid,
                                title: movedInventory.title,
                                [PREVIEW_URL_FIELD]: movedInventory.previewUrl,
                                amount: movedInventory.amount,
                                [SORT_INDEX_FIELD]: variables.newIndex,
                                status: movedInventory.status,
                                [FREE_DATE_FIELD]: movedInventory.freeDate,
                            },
                        })
                );
            }

            return { previousEquipmentData };
        },
        onError: (_error, _variables, context) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(
                equipmentDataQueryKey,
                (previous) => context?.previousEquipmentData ?? previous
            );
        },
        onSuccess: (_data, variables) => {
            window.setTimeout(() => {
                void invalidateInventoryActionTotals([warehouse.id, variables.toWarehouseId]);
            }, 1000);
        },
    });

    const partiallyMoveInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.partiallyMoveInventory,
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: equipmentDataQueryKey });

            return {
                previousEquipmentData:
                    queryClient.getQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey),
            };
        },
        onSuccess: async (data, variables) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                partiallyMoveInventoryInEquipmentCache({
                    previous,
                    inventoryGuid: variables.inventoryGuid,
                    fromWarehouseId: warehouse.id,
                    toWarehouseId: variables.toWarehouseId,
                    newIndex: variables.sortIndex,
                    quantity: variables.quantity,
                    createdInventory: data.data.response,
                })
            );

            await invalidateInventoryActionTotals([warehouse.id, variables.toWarehouseId]);
        },
        onError: (_error, _variables, context) => {
            if (context?.previousEquipmentData) {
                queryClient.setQueryData(equipmentDataQueryKey, context.previousEquipmentData);
            }
        },
    });

    const updateInventoryStatusMutation = useMutation({
        mutationFn: equipmentInventoryApi.updateInventoryStatus,
        onSuccess: async (_data, variables) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                updateShortInventoryInCache({
                    previous,
                    warehouseId: warehouse.id,
                    inventoryGuid: variables.inventoryGuid,
                    patch: {
                        status: variables.status,
                        [FREE_DATE_FIELD]: undefined,
                    },
                })
            );

            await invalidateInventoryActionTotals([warehouse.id]);
        },
    });

    const deleteInventoryMutation = useMutation({
        mutationFn: equipmentInventoryApi.removeInventory,
        onSuccess: async (_data, variables) => {
            queryClient.setQueryData<EquipmentDataQueryCache>(equipmentDataQueryKey, (previous) =>
                removeShortInventoryFromCache({
                    previous,
                    warehouseId: warehouse.id,
                    inventoryGuid: variables.inventoryGuid,
                })
            );

            await invalidateInventoryActionTotals([warehouse.id]);
        },
    });

    const openInventoryModal = (inventoryGuid: string) => {
        setInventoryModalMode('edit');
        setOpenedInventoryGuid(inventoryGuid);
        setIsInventoryModalOpen(true);
    };

    const closeInventoryModal = () => {
        setIsInventoryModalOpen(false);
    };

    const clearClosedInventoryModal = () => {
        setOpenedInventoryGuid(null);
        setInventoryModalMode('edit');
    };

    const moveInventory = (
        inventoryGuid: string,
        toWarehouse: UiDropdownSearchListItem,
        closeDropdown: () => void
    ) => {
        const targetWarehouse = warehouses.find(
            (warehouseItem) => warehouseItem.id === toWarehouse.id
        );
        const movedInventory = warehouse.items.find((item) => item.guid === inventoryGuid);
        const newIndex = targetWarehouse?.items.length ?? 0;

        if (movedInventory && movedInventory.amount > 1) {
            requestMove({
                amount: movedInventory.amount,
                onMoveFully: () =>
                    moveInventoryMutation.mutateAsync({
                        inventoryGuid,
                        toWarehouseId: toWarehouse.id,
                        newIndex,
                    }),
                onMovePartially: (quantity) =>
                    partiallyMoveInventoryMutation.mutateAsync({
                        inventoryGuid,
                        toWarehouseId: toWarehouse.id,
                        sortIndex: newIndex,
                        quantity,
                    }),
            });
            closeDropdown();

            return;
        }

        moveInventoryMutation.mutate({
            inventoryGuid,
            toWarehouseId: toWarehouse.id,
            newIndex,
        });
        closeDropdown();
    };

    const changeInventoryStatus =
        (inventoryGuid: string) =>
        (status: EquipmentInventoryStatus): React.MouseEventHandler<HTMLLIElement> =>
        (event) => {
            event.stopPropagation();
            updateInventoryStatusMutation.mutate({
                inventoryGuid,
                status,
            });
        };

    const deleteInventory =
        (inventoryGuid: string): React.MouseEventHandler<HTMLLIElement> =>
        (event) => {
            event.stopPropagation();
            setInventoryGuidForDelete(inventoryGuid);
        };

    const confirmDeleteInventory = () => {
        if (!inventoryGuidForDelete) {
            return;
        }

        deleteInventoryMutation.mutate(
            { inventoryGuid: inventoryGuidForDelete },
            {
                onSuccess: () => setInventoryGuidForDelete(null),
            }
        );
    };

    return {
        openedInventoryGuid,
        isInventoryModalOpen,
        inventoryGuidForDelete,
        inventoryModalMode,
        isCreateInventoryPending: createInventoryMutation.isPending,
        isDeleteInventoryPending: deleteInventoryMutation.isPending,
        createInventory: () => createInventoryMutation.mutate({ warehouseId: warehouse.id }),
        copyInventory: (inventoryGuid: string) => copyInventoryMutation.mutate({ inventoryGuid }),
        openInventoryModal,
        closeInventoryModal,
        clearClosedInventoryModal,
        moveInventory,
        changeInventoryStatus,
        deleteInventory,
        confirmDeleteInventory,
        closeDeleteInventoryPopup: () => setInventoryGuidForDelete(null),
    };
};
